import { Box, Container, useTheme } from "@mui/material";
import { WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import SnailSection from "../Shared/SnailSection";
import StrengthWeaknessSection from "../Shared/StrengthWeaknessSection";
import UpdateAndHistorySection from "../Shared/UpdateAndHistorySection";
import EducationSection from "../Shared/Education/EducationSection";
import useCyclingFitnessProfile from "./useCyclingFitnessProfile";
import ManualCyclingProfileUpdateDialog from "./ManualCyclingProfileUpdateDialog";
import { t } from "@lingui/macro";
import { WfIconWorkoutTypesBikingRoad } from "@WahooFitness/redesignr";
import { useMemo } from "react";
import { SourcePage } from "@/services/Analytics/EventTypes";
import { WfCardProps } from "@WahooFitness/redesignr/Components/WfCard/WfCard.js";

const CyclingProfile = ({ educationCards }: { educationCards: WfCardProps[] }) => {
  const { palette } = useTheme();
  const {
    riderStrength: strength,
    riderWeakness: weakness,
    riderTypeName: profileTypeName,
    riderTypeIcon: profileTypeIcon,
    profileDescription,
    dimensions,
    legendItems,
    hasProfile,
    openInfoDrawer,
    isRiderTypeEstimate,
    isRiderTypeManual,
    openEstimateExplanationDrawer,
    hasRiderTypeMismatch,
    handleDismissRiderTypeAlert,
    userDismissedRiderTypeAlert,
    handleOpenHistoryDrawer,
    openUpdateProfileDrawer,
    editProfileDrawerOpen,
    handleEditDrawerClose,
    cyclingFitnessProfilesData,
  } = useCyclingFitnessProfile();

  const analyticsContext = useMemo(
    () => ({
      name: "athlete_profile_cycling",
      data: { source_page_name: SourcePage.athlete_profile_cycling },
    }),
    []
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "100%" }}>
      {cyclingFitnessProfilesData && (
        <ManualCyclingProfileUpdateDialog
          open={editProfileDrawerOpen}
          handleClose={handleEditDrawerClose}
          cyclingFitnessProfilesData={cyclingFitnessProfilesData}
        />
      )}
      <Box
        sx={{
          backgroundColor: palette.background.level1,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          maxHeight: "100%",
          overflow: "auto",
        }}
      >
        <Container
          maxWidth="sm"
          disableGutters
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1.5,
            pt: 2,
          }}
        >
          <SnailSection
            workoutFamily={WahooWorkoutTypeFamily.Cycling}
            hasProfile={hasProfile}
            profileTypeName={profileTypeName || t`Not set`}
            profileTypeIcon={profileTypeIcon}
            profileDescription={profileDescription}
            dimensions={dimensions}
            legendItems={legendItems}
            openInfoDrawer={openInfoDrawer}
            isProfileEstimate={isRiderTypeEstimate}
            isProfileManual={isRiderTypeManual}
            openEstimateExplanationDrawer={openEstimateExplanationDrawer}
            hasRiderTypeMismatch={!!hasRiderTypeMismatch}
            handleDismissRiderTypeAlert={handleDismissRiderTypeAlert}
            userDismissedRiderTypeAlert={userDismissedRiderTypeAlert}
          />
          <StrengthWeaknessSection
            strength={strength}
            weakness={weakness}
            analyticsContext={analyticsContext}
          />
          <UpdateAndHistorySection
            openUpdateProfileDrawer={openUpdateProfileDrawer}
            hasProfile={hasProfile}
            showHistoryButton={hasProfile}
            historyButtonText={t`Cycling Profile history`}
            navigateToHistory={handleOpenHistoryDrawer}
            buttonIcon={<WfIconWorkoutTypesBikingRoad fontSize="medium" />}
          />
        </Container>
      </Box>
      <EducationSection educationCards={educationCards} />
    </Box>
  );
};

export default CyclingProfile;
